import React from 'react';
import {
  TextField,
  Checkbox,
  MenuItem,
  FormControlLabel,
} from '@material-ui/core';
import CreateContentField from './CreateContentField';
import ClearIcon from '@material-ui/icons/Clear';
import { SingleOrderDiscount, DiscountValueType, OrderType } from './types';
import { useDebouncedState } from '../useDebouncedState';

const ORDER_TYPE_NONE = '__NONE__';

type Props = {
  discountItem: SingleOrderDiscount;
  index: number;
  errors: any;
  onChange: (
    index: number,
    field: keyof SingleOrderDiscount,
    value: any,
  ) => void;
  onRemove: (index: number) => void;
};

const SingleOrderDiscountRow = React.memo(
  ({ discountItem, index, errors, onChange, onRemove }: Props) => {
    const { value: localDiscountItem, setValue: setLocalDiscountItem } =
      useDebouncedState({
        value: discountItem,
        onChange: (newValue) => {
          Object.keys(newValue).forEach((key) => {
            const field = key as keyof SingleOrderDiscount;
            if (newValue[field] !== discountItem[field]) {
              onChange(index, field, newValue[field]);
            }
          });
        },
        delay: 300,
      });

    const handleChange = (field: keyof SingleOrderDiscount, value: any) => {
      setLocalDiscountItem({ ...localDiscountItem, [field]: value });
    };

    return (
      <div className='sod-card'>
        <div className='sod-card__header'>
          <span className='sod-card__title'>Discount {index + 1}</span>
          <FormControlLabel
            className='sod-toggle'
            control={
              <Checkbox
                color='secondary'
                checked={localDiscountItem.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
            }
            label='Active'
          />
          <button
            type='button'
            className='content__delete-btn sod-card__delete'
            onClick={() => onRemove(index)}
            aria-label='Remove discount'
          >
            <ClearIcon />
          </button>
        </div>

        <div className='sod-grid'>
          <CreateContentField
            fieldTitle='Title'
            field={
              <TextField
                fullWidth
                size='small'
                variant='outlined'
                value={localDiscountItem.title}
                onChange={(e) => handleChange('title', e.target.value)}
                error={!!errors?.title}
                helperText={errors?.title}
              />
            }
          />
          <CreateContentField
            fieldTitle='Label'
            field={
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                value={localDiscountItem.label}
                onChange={(e) => handleChange('label', e.target.value)}
              />
            }
          />
          <CreateContentField
            fieldTitle='Order Type'
            field={
              <TextField
                fullWidth
                select
                variant='outlined'
                size='small'
                value={localDiscountItem.orderType ?? ORDER_TYPE_NONE}
                onChange={(e) =>
                  handleChange(
                    'orderType',
                    e.target.value === ORDER_TYPE_NONE
                      ? undefined
                      : (e.target.value as OrderType),
                  )
                }
              >
                <MenuItem value={ORDER_TYPE_NONE}>No Type</MenuItem>
                <MenuItem value='WIN'>WIN</MenuItem>
                <MenuItem value='DEAL'>DEAL</MenuItem>
                <MenuItem value='DISCOUNT'>DISCOUNT</MenuItem>
                <MenuItem value='OFFER'>OFFER</MenuItem>
              </TextField>
            }
          />
          <CreateContentField
            fieldTitle='Discount Value Type'
            field={
              <TextField
                fullWidth
                select
                variant='outlined'
                size='small'
                value={localDiscountItem.discountValueType ?? 'FIXED_AMOUNT'}
                onChange={(e) =>
                  handleChange(
                    'discountValueType',
                    e.target.value === ORDER_TYPE_NONE
                      ? undefined
                      : (e.target.value as OrderType),
                  )
                }
              >
                <MenuItem value='FIXED_AMOUNT'>FIXED_AMOUNT</MenuItem>
                <MenuItem value='PERCENT'>PERCENT</MenuItem>
              </TextField>
            }
          />
          <CreateContentField
            fieldTitle='Source'
            field={
              <TextField
                fullWidth
                select
                variant='outlined'
                size='small'
                value={localDiscountItem.source}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('source', e.target.value)
                }
              >
                <MenuItem value='ALL'>ALL</MenuItem>
              </TextField>
            }
          />
        </div>

        <div className='sod-grid sod-grid--pricing'>
          <FormControlLabel
            className='sod-toggle sod-toggle--field'
            control={
              <Checkbox
                color='secondary'
                checked={!!localDiscountItem.hideFullPrice}
                onChange={(e) =>
                  handleChange('hideFullPrice', e.target.checked)
                }
              />
            }
            label='Hide full price'
          />
          <CreateContentField
            fieldTitle='Num Of Tickets'
            field={
              <TextField
                fullWidth
                variant='outlined'
                type='number'
                size='small'
                value={localDiscountItem.numOfTickets}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('numOfTickets', e.target.value)
                }
                error={!!errors?.numOfTickets}
                helperText={errors?.numOfTickets}
              />
            }
          />
          <CreateContentField
            fieldTitle='Total cost'
            field={
              <TextField
                fullWidth
                variant='outlined'
                type='number'
                size='small'
                value={localDiscountItem.totalCost}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('totalCost', e.target.value)
                }
                error={!!errors?.totalCost}
                helperText={errors?.totalCost}
              />
            }
          />
          <CreateContentField
            fieldTitle='Discount'
            field={
              <TextField
                fullWidth
                variant='outlined'
                size='small'
                value={localDiscountItem.discountValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('discountValue', e.target.value)
                }
                error={!!errors?.discountValue}
                helperText={errors?.discountValue}
              />
            }
          />
        </div>
      </div>
    );
  },
);

export default SingleOrderDiscountRow;
